import React, { useState } from 'react';
import { ArrowRight, Heart, Camera, Play, Instagram, Star, Truck, RefreshCw, Shield, Eye, Image } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Template 14: STUDIO
 * Style: Visual Portfolio, Photography-focused, Creative
 * Best for: Designer portfolios, Lookbook showcases, Fashion photography brands
 * Light theme with violet/purple creative accents
 */
const Template14 = () => {
    const navigate = useNavigate();
    const [selectedImage, setSelectedImage] = useState(null);

    // Portfolio gallery
    const portfolio = [
        { id: 1, title: 'Monsoon Collection', category: 'Editorial', image: 'https://placehold.co/600x800/F5F3FF/6D28D9?text=Editorial+1', aspect: 'tall' },
        { id: 2, title: 'Urban Streets', category: 'Streetwear', image: 'https://placehold.co/600x600/EDE9FE/7C3AED?text=Street+1', aspect: 'square' },
        { id: 3, title: 'Festive Dreams', category: 'Occasion', image: 'https://placehold.co/600x400/DDD6FE/8B5CF6?text=Festive+1', aspect: 'wide' },
        { id: 4, title: 'Contemporary Fusion', category: 'Indo-Western', image: 'https://placehold.co/600x800/F5F3FF/6D28D9?text=Fusion+1', aspect: 'tall' },
        { id: 5, title: 'Minimalist Edit', category: 'Basics', image: 'https://placehold.co/600x600/EDE9FE/7C3AED?text=Minimal+1', aspect: 'square' },
        { id: 6, title: 'Bridal Couture', category: 'Wedding', image: 'https://placehold.co/600x800/DDD6FE/8B5CF6?text=Bridal+1', aspect: 'tall' },
        { id: 7, title: 'Resort Wear', category: 'Summer', image: 'https://placehold.co/600x400/F5F3FF/6D28D9?text=Resort+1', aspect: 'wide' },
        { id: 8, title: 'Office Elegance', category: 'Workwear', image: 'https://placehold.co/600x600/EDE9FE/7C3AED?text=Office+1', aspect: 'square' }
    ];

    // Featured products
    const products = [
        { id: 1, name: 'Printed Silk Kaftan', price: '₹4,999', image: 'https://placehold.co/400x500/F5F3FF/6D28D9?text=Kaftan+1' },
        { id: 2, name: 'Embroidered Jacket', price: '₹6,499', image: 'https://placehold.co/400x500/EDE9FE/7C3AED?text=Jacket+1' },
        { id: 3, name: 'Asymmetric Kurta', price: '₹3,299', image: 'https://placehold.co/400x500/DDD6FE/8B5CF6?text=Kurta+1' },
        { id: 4, name: 'Fusion Dress', price: '₹5,799', image: 'https://placehold.co/400x500/F5F3FF/6D28D9?text=Dress+1' }
    ];

    // Behind the scenes
    const behindScenes = [
        { title: 'Design Process', image: 'https://placehold.co/400x300/F5F3FF/6D28D9?text=Process' },
        { title: 'Fabric Selection', image: 'https://placehold.co/400x300/EDE9FE/7C3AED?text=Fabric' },
        { title: 'Photo Shoot', image: 'https://placehold.co/400x300/DDD6FE/8B5CF6?text=Shoot' }
    ];

    // Testimonials
    const testimonials = [
        { text: 'The attention to detail in their designs is remarkable. Each piece feels like a work of art.', name: 'Priya Sharma', role: 'Fashion Blogger', location: 'Mumbai' },
        { text: 'Studio\'s lookbooks are my go-to inspiration. Their aesthetic is truly unique.', name: 'Ananya Iyer', role: 'Stylist', location: 'Bangalore' }
    ];

    return (
        <div className="w-full min-h-screen bg-white font-sans">
            {/* Hero Section - Visual Impact */}
            <section className="relative min-h-screen overflow-hidden bg-gradient-to-br from-violet-50 via-purple-50/50 to-fuchsia-50/30">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="grid lg:grid-cols-2 gap-12 items-center min-h-screen py-16">
                        {/* Text Content */}
                        <div className="text-center lg:text-left">
                            <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-100 rounded-full mb-6">
                                <Camera className="w-4 h-4 text-violet-600" />
                                <span className="text-violet-700 text-sm font-medium">Visual Portfolio</span>
                            </div>
                            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-light text-violet-950 leading-[1.0] mb-6">
                                Where Fashion
                                <span className="block font-serif italic text-violet-600">Meets Art</span>
                            </h1>
                            <p className="text-violet-700/70 text-lg lg:text-xl max-w-lg mx-auto lg:mx-0 mb-8 leading-relaxed">
                                A curated visual journey through contemporary Indian design.
                                Every frame tells a story, every piece is a statement.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                                <button
                                    onClick={() => navigate('/template14/shop')}
                                    className="group px-8 py-4 bg-violet-600 text-white text-sm font-medium tracking-wider uppercase hover:bg-violet-700 transition-all duration-300 rounded-full flex items-center justify-center gap-3"
                                >
                                    Explore Portfolio
                                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </button>
                                <button className="group px-8 py-4 border-2 border-violet-300 text-violet-700 text-sm font-medium tracking-wider uppercase hover:border-violet-600 transition-all duration-300 rounded-full flex items-center justify-center gap-3">
                                    <Play className="w-4 h-4" />
                                    Watch Lookbook
                                </button>
                            </div>
                        </div>

                        {/* Hero Gallery Grid */}
                        <div className="relative">
                            <div className="grid grid-cols-12 gap-3 max-w-xl mx-auto">
                                <div className="col-span-7 space-y-3">
                                    <div className="aspect-[4/5] overflow-hidden rounded-2xl shadow-xl">
                                        <img
                                            src="https://placehold.co/400x500/F5F3FF/6D28D9?text=Hero+1"
                                            alt="Portfolio"
                                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                                        />
                                    </div>
                                    <div className="aspect-[4/3] overflow-hidden rounded-2xl shadow-xl">
                                        <img
                                            src="https://placehold.co/400x300/EDE9FE/7C3AED?text=Hero+2"
                                            alt="Portfolio"
                                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                                        />
                                    </div>
                                </div>
                                <div className="col-span-5 space-y-3 pt-8">
                                    <div className="aspect-square overflow-hidden rounded-2xl shadow-xl">
                                        <img
                                            src="https://placehold.co/300x300/DDD6FE/8B5CF6?text=Hero+3"
                                            alt="Portfolio"
                                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                                        />
                                    </div>
                                    <div className="aspect-[3/4] overflow-hidden rounded-2xl shadow-xl">
                                        <img
                                            src="https://placehold.co/300x400/F5F3FF/6D28D9?text=Hero+4"
                                            alt="Portfolio"
                                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                                        />
                                    </div>
                                </div>
                            </div>
                            {/* Floating Stats */}
                            <div className="absolute -left-4 bottom-20 bg-white shadow-xl px-5 py-4 rounded-xl z-20">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center">
                                        <Image className="w-5 h-5 text-violet-600" />
                                    </div>
                                    <div>
                                        <p className="text-violet-950 font-bold">200+</p>
                                        <p className="text-violet-600 text-xs">Looks Created</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Portfolio Gallery - Masonry */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12 lg:mb-16">
                        <span className="text-violet-600 text-sm font-medium tracking-[0.2em] uppercase">The Gallery</span>
                        <h2 className="text-3xl lg:text-4xl font-light text-violet-950 mt-3">Visual Stories</h2>
                        <p className="text-violet-600/70 mt-4 max-w-xl mx-auto">
                            A curated collection of our finest work, capturing the essence of contemporary Indian fashion
                        </p>
                    </div>

                    {/* Masonry Grid */}
                    <div className="columns-2 lg:columns-3 xl:columns-4 gap-4 space-y-4">
                        {portfolio.map((item) => (
                            <div
                                key={item.id}
                                className="break-inside-avoid group cursor-pointer"
                                onClick={() => setSelectedImage(item)}
                            >
                                <div className={`relative overflow-hidden rounded-xl bg-violet-100 ${
                                    item.aspect === 'tall' ? 'aspect-[3/4]' :
                                    item.aspect === 'wide' ? 'aspect-[4/3]' : 'aspect-square'
                                }`}>
                                    <img
                                        src={item.image}
                                        alt={item.title}
                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                    />
                                    <div className="absolute inset-0 bg-violet-900/0 group-hover:bg-violet-900/40 transition-colors duration-300" />
                                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                        <Eye className="w-8 h-8 text-white" />
                                    </div>
                                    <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-violet-950/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                        <p className="text-violet-200 text-xs uppercase tracking-wider">{item.category}</p>
                                        <p className="text-white font-medium">{item.title}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Quote Section */}
            <section className="py-20 lg:py-28 bg-violet-600 text-white">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <p className="text-3xl lg:text-4xl font-light leading-relaxed">
                        "Fashion is the armor to survive the reality of everyday life.
                        We create that armor with <span className="font-serif italic">art and passion</span>."
                    </p>
                    <p className="text-violet-200 mt-8">— Our Design Philosophy</p>
                </div>
            </section>

            {/* Featured Products */}
            <section className="py-16 lg:py-24 bg-violet-50/50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-12 lg:mb-16">
                        <div>
                            <span className="text-violet-600 text-sm font-medium tracking-[0.2em] uppercase">Shop The Edit</span>
                            <h2 className="text-3xl lg:text-4xl font-light text-violet-950 mt-3">Featured Pieces</h2>
                        </div>
                        <button
                            onClick={() => navigate('/template14/shop')}
                            className="group flex items-center gap-2 text-violet-700 font-medium mt-4 lg:mt-0 hover:text-violet-900 transition-colors"
                        >
                            View All
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                        {products.map((product) => (
                            <div
                                key={product.id}
                                onClick={() => navigate('/template14/shop')}
                                className="group cursor-pointer"
                            >
                                <div className="relative aspect-[3/4] overflow-hidden bg-violet-100 rounded-xl mb-4">
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                    <button className="absolute top-3 right-3 w-9 h-9 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                                        <Heart className="w-4 h-4 text-violet-600" />
                                    </button>
                                </div>
                                <h3 className="text-violet-950 font-medium group-hover:text-violet-600 transition-colors">
                                    {product.name}
                                </h3>
                                <p className="text-violet-600 font-semibold mt-1">{product.price}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Behind The Scenes */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                        <div>
                            <span className="text-violet-600 text-sm font-medium tracking-[0.2em] uppercase">Behind The Scenes</span>
                            <h2 className="text-3xl lg:text-4xl font-light text-violet-950 mt-3 mb-6">
                                The Creative Process
                            </h2>
                            <p className="text-violet-700/80 text-lg leading-relaxed mb-8">
                                Every collection begins with a story. From mood boards to fabric selection,
                                from sketches to final stitches — we invite you into our world of creativity.
                            </p>
                            <p className="text-violet-700/80 leading-relaxed mb-8">
                                Our studio in Mumbai houses a team of passionate designers, artisans,
                                and storytellers who collaborate to bring each vision to life.
                            </p>
                            <button
                                onClick={() => navigate('/template14/shop')}
                                className="group inline-flex items-center gap-3 text-violet-700 font-medium hover:text-violet-900 transition-colors"
                            >
                                Explore Our Story
                                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </button>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            {behindScenes.map((scene, index) => (
                                <div
                                    key={index}
                                    className={`group cursor-pointer ${index === 2 ? 'col-span-2' : ''}`}
                                >
                                    <div className={`relative overflow-hidden rounded-xl ${index === 2 ? 'aspect-video' : 'aspect-square'}`}>
                                        <img
                                            src={scene.image}
                                            alt={scene.title}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                        />
                                        <div className="absolute inset-0 bg-violet-900/20 group-hover:bg-violet-900/40 transition-colors" />
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <Play className="w-12 h-12 text-white opacity-70 group-hover:opacity-100 transition-opacity" />
                                        </div>
                                        <p className="absolute bottom-4 left-4 text-white font-medium">{scene.title}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section className="py-16 lg:py-24 bg-gradient-to-br from-violet-50 to-fuchsia-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12 lg:mb-16">
                        <span className="text-violet-600 text-sm font-medium tracking-[0.2em] uppercase">Voices</span>
                        <h2 className="text-3xl lg:text-4xl font-light text-violet-950 mt-3">What They Say</h2>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8">
                        {testimonials.map((testimonial, index) => (
                            <div key={index} className="bg-white p-8 lg:p-10 rounded-2xl shadow-sm">
                                <div className="flex gap-1 mb-6">
                                    {[...Array(5)].map((_, i) => (
                                        <Star key={i} className="w-4 h-4 fill-violet-400 text-violet-400" />
                                    ))}
                                </div>
                                <p className="text-violet-800/80 text-lg leading-relaxed mb-8 italic">
                                    "{testimonial.text}"
                                </p>
                                <div className="flex items-center gap-4">
                                    <div className="w-14 h-14 bg-violet-100 rounded-full flex items-center justify-center">
                                        <span className="text-violet-600 font-semibold text-lg">{testimonial.name[0]}</span>
                                    </div>
                                    <div>
                                        <p className="font-medium text-violet-950">{testimonial.name}</p>
                                        <p className="text-violet-600 text-sm">{testimonial.role}</p>
                                        <p className="text-violet-400 text-xs">{testimonial.location}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Newsletter */}
            <section className="py-16 lg:py-24 bg-violet-950 text-white">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <Camera className="w-10 h-10 text-violet-400 mx-auto mb-6" />
                    <h2 className="text-3xl lg:text-4xl font-light text-white mb-4">
                        Join The Studio
                    </h2>
                    <p className="text-violet-300 text-lg mb-8">
                        Get exclusive access to new lookbooks, behind-the-scenes content, and first looks at upcoming collections.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
                        <input
                            type="email"
                            placeholder="Enter your email"
                            className="flex-1 px-6 py-4 bg-violet-900 border border-violet-700 rounded-full text-white placeholder-violet-400 focus:outline-none focus:border-violet-500 transition-colors"
                        />
                        <button className="px-8 py-4 bg-violet-500 text-white text-sm font-semibold tracking-wider uppercase hover:bg-violet-400 transition-colors rounded-full whitespace-nowrap">
                            Subscribe
                        </button>
                    </div>
                </div>
            </section>

            {/* Instagram */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <div className="flex items-center justify-center gap-3 mb-3">
                            <Instagram className="w-6 h-6 text-violet-600" />
                            <span className="text-violet-600 font-medium">@studio_fashion</span>
                        </div>
                        <h2 className="text-3xl lg:text-4xl font-light text-violet-950">Follow The Journey</h2>
                    </div>

                    <div className="grid grid-cols-3 md:grid-cols-6 gap-2 lg:gap-4">
                        {[1, 2, 3, 4, 5, 6].map((_, index) => (
                            <div key={index} className="group relative aspect-square overflow-hidden rounded-lg cursor-pointer">
                                <img
                                    src={`https://placehold.co/300x300/F5F3FF/6D28D9?text=Insta+${index + 1}`}
                                    alt={`Instagram ${index + 1}`}
                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                />
                                <div className="absolute inset-0 bg-violet-600/0 group-hover:bg-violet-600/30 transition-colors duration-300 flex items-center justify-center">
                                    <Heart className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services */}
            <section className="py-12 bg-violet-50 border-t border-violet-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                                <Truck className="w-5 h-5 text-violet-600" />
                            </div>
                            <div>
                                <p className="font-medium text-violet-950">Free Shipping</p>
                                <p className="text-violet-600 text-sm">Above ₹2,999</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                                <RefreshCw className="w-5 h-5 text-violet-600" />
                            </div>
                            <div>
                                <p className="font-medium text-violet-950">Easy Returns</p>
                                <p className="text-violet-600 text-sm">15-day policy</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                                <Shield className="w-5 h-5 text-violet-600" />
                            </div>
                            <div>
                                <p className="font-medium text-violet-950">Secure Payment</p>
                                <p className="text-violet-600 text-sm">100% secure</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                                <Camera className="w-5 h-5 text-violet-600" />
                            </div>
                            <div>
                                <p className="font-medium text-violet-950">Styling Service</p>
                                <p className="text-violet-600 text-sm">Expert advice</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Image Modal */}
            {selectedImage && (
                <div
                    className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
                    onClick={() => setSelectedImage(null)}
                >
                    <div className="max-w-4xl w-full">
                        <img
                            src={selectedImage.image}
                            alt={selectedImage.title}
                            className="w-full h-auto rounded-lg"
                        />
                        <div className="mt-4 text-center">
                            <p className="text-violet-300 text-sm uppercase tracking-wider">{selectedImage.category}</p>
                            <p className="text-white text-xl font-medium mt-1">{selectedImage.title}</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Template14;
